import React, { useState, useReducer, useEffect, createContext, useContext } from 'react'
import api from 'api'
import { logout as logoutFromMPDC } from 'phonecom'
import { stubDevices } from './mock'
import { passwordSecurityReducer, deviceReducer, clearSessionsReducer, DeviceReducerState, ClearSessionsReducerState, PasswordSecurityInterface, PasswordSecurityReducerState, passwordSecurityDefaultData, deviceDefaultData, clearSessionsDefaultData } from './reducers'
import AccountSecurityClient from './AccountSecurityClient'
import { usePhoenixSessionContext, UserContext } from 'providers'

/**
 *
 */
export { stubDevices } from './mock'

const MFA_FEATURE = 'mfa.signin_email_required_new_customers'

/**
 *
 */
export type DeviceInterface = {
    id: number
    name: string
    currentDevice?: boolean
    location: string
    date: string
    type: 'Desktop' | 'Mobile'
}

type ResetTypes = 'PASSWORD_DATA' | 'DEVICES' | 'CLEAR_SESSIONS'

interface ContextInterface {
    passwordSecurityState: PasswordSecurityReducerState,
    devices: DeviceReducerState, setDevices: (revalidate?: boolean) => Promise<DeviceInterface[]>,
    clearSessions: ClearSessionsReducerState, clearDeviceSessions: (clearCurrentSession?: boolean, logout?: () => any) => Promise<any>,
    reset: (type: ResetTypes) => void,
    client: AccountSecurityClient & { enableMFA: (options?: { requestNewSession?: boolean }) => Promise<any>, _sessionToken: string } // eslint-disable-line @typescript-eslint/naming-convention
}

/**
 *
 */
export const AccountSecurityContext = createContext<ContextInterface>(null)

/**
 *
 */
export const useAccountSecurityContext = () => {
    const contextData = useContext(AccountSecurityContext)
    if (!contextData) {
        throw new Error('useAccountSecurityContext must be used inside AccountSecurityProvider tags')
    }

    return contextData
}

/**
 *
 */
export const AccountSecurityProvider = ({ children }: { children: JSX.Element }) => {
    const [passwordSecurityState, dispatchPasswordSecurity] = useReducer(passwordSecurityReducer, { data: passwordSecurityDefaultData, isLoading: true, error: '' })
    const [devices, dispatchDevices] = useReducer(deviceReducer, { data: deviceDefaultData, isLoading: false, error: '' })
    const [clearSessions, dispatchClearSessions] = useReducer(clearSessionsReducer, { data: clearSessionsDefaultData, isLoading: false, error: '' })
    const { session } = usePhoenixSessionContext()
    const [client, setClient] = useState(new AccountSecurityClient())
    const user = useContext(UserContext)

    const getData = (response: Record<string, any>): PasswordSecurityInterface => {
        return response.data
    }

    const makeDate = () => {
        const date = new Date()
        date.setDate(date.getDate() + 20)
        return date.toISOString()
    }

    const setPasswordSecurityData = async () => {
        let data

        try {
            dispatchPasswordSecurity({ type: 'SET_LOADING', payload: true })

            const isMfaEnabled = user?.features?.includes(MFA_FEATURE)
            const mfaEnableLastDate = user?.mfa_grace_period_end

            const response = await new Promise((resolve, reject) => {
                resolve({
                    data: {
                        hasPasswordExpired: false,
                        daysRemainedForPasswordExpiry: 20,
                        passwordExpiryDate: makeDate(),
                        passwordExpiryDialogShown: false,
                        isMfaEnabled,
                        mfaEnableLastDate
                    }
                })
            })
            data = getData(response)

            dispatchPasswordSecurity({ type: 'SET_DATA', payload: data })
        } catch (error) {
            console.error('Failed to fetch password related data:', error)
            dispatchPasswordSecurity({ type: 'SET_ERROR', payload: error.message })
        } finally {
            dispatchPasswordSecurity({ type: 'SET_LOADING', payload: false })
        }

        return data
    }

    const setDevices = async (revalidate = false) => {
        if (!revalidate && devices.data.length) return devices.data

        let fetchedDevices

        try {
            dispatchDevices({ type: 'SET_LOADING', payload: true })

            const response = await api.fetchActiveSessions()
            fetchedDevices = response.data || stubDevices

            dispatchDevices({ type: 'SET_DATA', payload: fetchedDevices })
        } catch (error) {
            console.error('Failed to fetch device session related data:', error)
            dispatchDevices({ type: 'SET_ERROR', payload: (error as Error).message })
        } finally {
            dispatchDevices({ type: 'SET_LOADING', payload: false })
        }

        return fetchedDevices
    }

    const clearDeviceSessions = async (clearCurrentSession = false, logoutCb) => {
        let data

        try {
            dispatchClearSessions({ type: 'SET_LOADING', payload: true })

            const logoutPromise = logoutCb ? logoutCb() : logoutFromMPDC(false)
            const response = await api.clearDeviceSessions()
            if (clearCurrentSession) await logoutPromise

            data = response.data || {}

            dispatchClearSessions({ type: 'SET_DATA', payload: data })
        } catch (error) {
            console.error('Failed to clear device sessions:', error)
            dispatchClearSessions({ type: 'SET_ERROR', payload: (error as Error).message })
        } finally {
            dispatchClearSessions({ type: 'SET_LOADING', payload: false })
        }

        return data
    }

    const reset = (type: ResetTypes) => {
        switch (type) {
                case 'PASSWORD_DATA':
                    dispatchPasswordSecurity({ type: 'RESET' })
                    break
                case 'DEVICES':
                    dispatchDevices({ type: 'RESET' })
                    break
                case 'CLEAR_SESSIONS':
                    dispatchClearSessions({ type: 'RESET' })
                    break
                default:
                    console.warn('Invalid Reset Type')
                    break
        }
    }

    const setSessionToken = () => {
        if (session.user?.token) {
            const newClient = new AccountSecurityClient()
            newClient.setSessionToken(session.user.token.split('Bearer ')[1])
            setClient(newClient)
        }
    }

    useEffect(() => {
        setPasswordSecurityData()
    }, [user])

    useEffect(() => {
        setSessionToken()
    }, [session.user?.token])

    return (
        <AccountSecurityContext.Provider value={{ passwordSecurityState, devices, setDevices, clearSessions, clearDeviceSessions, reset, client }}>
            {children}
        </AccountSecurityContext.Provider>
    )
}

/**
 *
 */
export { passwordSecurityDefaultData, clearSessionsDefaultData } from './reducers'
